import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../components/ConfirmDialog';
import { getHealthHouseMockConfig, resetHealthHouseMockConfig, updateHealthHouseMockConfig } from '../api/healthHouseMockApi';
import './HealthHouseMockConfig.css';

const editableConfig = (config) => ({ projects: Object.fromEntries(
  Object.entries(config?.projects || {}).map(([code, setting]) => [code, { values: { ...setting.values }, usage: setting.usage }])
) });
const signature = (config) => JSON.stringify(Object.entries(editableConfig(config).projects).sort().map(([code, s]) => (
  [code, s.usage, Object.entries(s.values).sort()]
)));
const copyConfig = (config) => JSON.parse(JSON.stringify(config));
const catalogSignature = (data) => JSON.stringify([data?.station?.device_model_id, data?.projects]);

export default function HealthHouseMockConfig() {
  const { t, i18n } = useTranslation();
  const { stationId } = useParams();
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState(null);
  const [draft, setDraft] = useState({ projects: {} });
  const [rearm, setRearm] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [remoteChanged, setRemoteChanged] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [picked, setPicked] = useState([]);
  const [confirmation, setConfirmation] = useState(null);
  const [resetRequested, setResetRequested] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const state = useRef({});
  const requestVersion = useRef(0);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const pickerRef = useRef(null);
  const dirty = !!snapshot && (signature(draft) !== signature(snapshot.config) || rearm.length > 0);
  state.current = { snapshot, dirty, saving };
  const text = (key, options) => t(`healthHouseMockConfig.${key}`, options);
  const label = (item) => i18n.language?.startsWith('zh') ? item.name_zh : item.name_en;

  const adopt = useCallback((data) => {
    setLoadFailed(false);
    setSnapshot(data);
    setDraft(copyConfig(data.config));
    setRearm([]);
    setRemoteChanged(false);
    setResetRequested(false);
    setShowValidation(false);
  }, []);

  const load = useCallback(async (poll = false) => {
    if (poll && (document.visibilityState === 'hidden' || state.current.saving || inFlight.current)) return;
    const version = ++requestVersion.current;
    inFlight.current = true;
    if (!poll) { setLoading(true); setLoadFailed(false); setError(''); }
    try {
      const data = await getHealthHouseMockConfig(stationId);
      if (!data?.station || !Array.isArray(data.projects) || !data.config?.projects) throw new Error('Unsupported settings response');
      if (!mounted.current || version !== requestVersion.current) return;
      setRefreshFailed(false);
      if (poll && state.current.dirty) {
        if (data.revision !== state.current.snapshot?.revision || catalogSignature(data) !== catalogSignature(state.current.snapshot)) setRemoteChanged(true);
      } else {
        adopt(data);
      }
    } catch (_error) {
      if (!mounted.current || version !== requestVersion.current) return;
      if (poll) setRefreshFailed(true);
      else setLoadFailed(true);
    } finally {
      if (mounted.current && version === requestVersion.current) {
        inFlight.current = false;
        if (!poll) setLoading(false);
      }
    }
  }, [stationId, adopt]);

  useEffect(() => {
    mounted.current = true;
    load();
    const interval = setInterval(() => load(true), 5000);
    const onVisible = () => load(true);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      mounted.current = false;
      requestVersion.current += 1;
      inFlight.current = false;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  useEffect(() => {
    const warn = (event) => { if (state.current.dirty) { event.preventDefault(); event.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);

  useEffect(() => {
    if (!pickerOpen) return undefined;
    const previousFocus = document.activeElement;
    const shell = pickerRef.current;
    const focusable = () => Array.from(shell.querySelectorAll('button:not(:disabled), input:not(:disabled)'));
    focusable()[0]?.focus();
    const onKey = (event) => {
      if (event.key === 'Escape') setPickerOpen(false);
      if (event.key !== 'Tab') return;
      const items = focusable();
      const first = items[0]; const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    shell.addEventListener('keydown', onKey);
    return () => { shell.removeEventListener('keydown', onKey); previousFocus?.focus(); };
  }, [pickerOpen]);

  const projects = snapshot?.projects || [];
  const configured = Object.keys(draft.projects).map((code) => (
    projects.find((project) => project.code === code) || { code, name_zh: text('unavailableProject'), name_en: text('unavailableProject'), supported: false, fields: [] }
  ));
  const issues = useMemo(() => {
    const found = {};
    for (const project of snapshot?.projects || []) {
      const setting = draft.projects[project.code];
      if (!setting || !project.supported) continue;
      if (!Object.keys(setting.values).length) found[project.code] = 'chooseValue';
      for (const field of project.fields) {
        if (!(field.key in setting.values)) continue;
        const value = setting.values[field.key];
        const number = Number(value);
        const factor = 10 ** field.precision;
        if (String(value).trim() === '' || !Number.isFinite(number) || number < field.min || number > (field.max ?? 1000000)
          || Math.abs(number * factor - Math.round(number * factor)) > 0.00001) found[`${project.code}.${field.key}`] = 'invalidValue';
      }
      if (project.code === '30' && setting.values.systolic_bp && setting.values.diastolic_bp
        && Number(setting.values.systolic_bp) <= Number(setting.values.diastolic_bp)) found['30.systolic_bp'] = 'pressureOrder';
    }
    return found;
  }, [draft, snapshot]);

  const changeProject = (code, change) => {
    setNotice(''); setError('');
    const next = change(draft.projects[code]);
    if (next.usage !== 'once') setRearm((current) => current.filter((key) => key !== code));
    setDraft((current) => {
      const original = snapshot.config.projects[code];
      const unchanged = signature({ projects: { [code]: next } }) === signature({ projects: original ? { [code]: original } : {} });
      return { projects: { ...current.projects, [code]: { ...next, consumed_at: unchanged ? original?.consumed_at : undefined } } };
    });
  };
  const removeProject = (code) => {
    setNotice(''); setError('');
    setDraft((current) => ({ projects: Object.fromEntries(Object.entries(current.projects).filter(([key]) => key !== code)) }));
    setRearm((current) => current.filter((key) => key !== code));
  };
  const save = async () => {
    if (!snapshot || saving || remoteChanged) return;
    setShowValidation(true);
    if (Object.keys(issues).length) { setError(text('fixValues')); return; }
    setSaving(true); state.current.saving = true;
    setError(''); setNotice('');
    const version = ++requestVersion.current;
    inFlight.current = false;
    try {
      const data = resetRequested && !Object.keys(draft.projects).length
        ? await resetHealthHouseMockConfig(stationId, { expected_revision: snapshot.revision })
        : await updateHealthHouseMockConfig(stationId, { expected_revision: snapshot.revision, config: editableConfig(draft), rearm_projects: rearm });
      if (!mounted.current || version !== requestVersion.current) return;
      adopt(data); setNotice(text('saved'));
    } catch (requestError) {
      if (!mounted.current || version !== requestVersion.current) return;
      const response = requestError?.response;
      if ((response?.status === 409 && response?.data?.code === 'revision_conflict') || response?.data?.reason === 'unsupported_project') setRemoteChanged(true);
      else setError(text(response?.status === 400 ? 'fixValues' : 'saveError'));
    } finally {
      if (mounted.current && version === requestVersion.current) { setSaving(false); state.current.saving = false; }
    }
  };
  const addProjects = () => {
    setDraft((current) => ({ projects: { ...current.projects, ...Object.fromEntries(picked.map((code) => (
      [code, { values: {}, usage: 'persistent' }]
    ))) } }));
    setPicked([]); setPickerOpen(false); setNotice('');
  };
  const leave = () => dirty ? setConfirmation('leave') : navigate('/health-house-stations');
  const confirm = () => {
    if (confirmation === 'leave') navigate('/health-house-stations');
    if (confirmation === 'reload') load();
    if (confirmation === 'reset') { setDraft({ projects: {} }); setRearm([]); setResetRequested(true); setNotice(''); setError(''); }
    setConfirmation(null);
  };
  const changedCodes = snapshot ? [...new Set([...Object.keys(snapshot.config.projects), ...Object.keys(draft.projects)])].filter((code) => (
    JSON.stringify(editableConfig(snapshot.config).projects[code]) !== JSON.stringify(editableConfig(draft).projects[code]) || rearm.includes(code)
  )) : [];

  if (loading || loadFailed) return <div className="demo-settings"><div className="demo-settings__empty" role={loadFailed ? 'alert' : 'status'}>
    <h1>{text(loadFailed ? 'loadError' : 'loading')}</h1>
    {loadFailed && <><p>{text('loadErrorHint')}</p><button className="theme-button theme-button--primary" onClick={() => load()}>{text('retry')}</button></>}
    <button className="theme-button theme-button--ghost" onClick={leave}>{text('back')}</button>
  </div></div>;

  return <div className="demo-settings">
    <header className="demo-settings__header">
      <button className="demo-settings__back" onClick={leave}>← {text('back')}</button>
      <h1>{text('title')}</h1>
      <p className="demo-settings__context">{snapshot.station.name} <span>·</span> {snapshot.station.clinic_name || text('unbound')}</p>
      <p className="demo-settings__intro">{text('intro')}</p>
    </header>
    {error && <div className="status-banner status-banner--error" role="alert">{error}</div>}
    {notice && <div className="status-banner status-banner--success" role="status">{notice}</div>}
    {remoteChanged && <div className="demo-settings__conflict" role="alert"><div><strong>{text('conflict')}</strong><p>{text('conflictHint')}</p></div>
      <button className="theme-button theme-button--secondary" onClick={() => setConfirmation('reload')}>{text('reload')}</button>
    </div>}
    {refreshFailed && <p className="demo-settings__muted" role="status">{text('refreshError')}</p>}
    {!snapshot.station.enabled && <p className="demo-settings__muted">{text('stationDisabled')}</p>}
    {!snapshot.station.device_model_id && <div className="status-banner status-banner--info">{text('noModel')}</div>}
    {!!snapshot.station.device_model_id && !projects.some((project) => project.supported) && <p className="demo-settings__muted">{text('noProjects')}</p>}
    <div className="demo-settings__toolbar">
      <div><h2>{text('results')}</h2><p>{configured.length ? text('projectCount', { count: configured.length }) : text('allAuto')}</p></div>
      <button className="theme-button theme-button--primary" onClick={() => { setPickerOpen(true); setPicked([]); }} disabled={saving || !projects.some((project) => project.supported && !draft.projects[project.code])}>{text('add')}</button>
    </div>
    {!configured.length && <div className="demo-settings__empty"><strong>{text('emptyTitle')}</strong><p>{text('emptyBody')}</p></div>}
    <div className="demo-settings__projects">
      {configured.map((project) => {
        const setting = draft.projects[project.code];
        const consumed = setting.usage === 'once' && setting.consumed_at && !rearm.includes(project.code);
        return <section className="demo-project" key={project.code} aria-labelledby={`project-${project.code}`}>
          <div className="demo-project__heading"><div><h3 id={`project-${project.code}`}>{label(project)}</h3><span className={`demo-project__state ${consumed ? 'is-consumed' : ''}`}>{text(consumed ? 'consumed' : setting.usage === 'once' ? 'pending' : 'continuous')}</span></div>
            <button className="theme-button theme-button--ghost theme-button--compact" onClick={() => removeProject(project.code)} disabled={saving}>{text('restoreAuto')}</button>
          </div>
          {!project.supported && <p className="demo-settings__muted">{text('unsupported')}</p>}
          {consumed && <div className="demo-project__used"><p>{text('consumedHint')}</p><button className="theme-button theme-button--secondary theme-button--compact" disabled={saving || !project.supported} onClick={() => { setRearm((current) => [...new Set([...current, project.code])]); setNotice(''); }}>{text('useAgain')}</button></div>}
          <div className="demo-project__fields">
            {project.fields.map((field) => {
              const id = `${project.code}-${field.key}`;
              const custom = Object.prototype.hasOwnProperty.call(setting.values, field.key);
              const issue = showValidation && issues[`${project.code}.${field.key}`];
              const bmi = project.code === '90' && field.derived && Number(setting.values.height) > 0 && Number(setting.values.weight) > 0
                ? (Number(setting.values.weight) / ((Number(setting.values.height) / 100) ** 2)).toFixed(1) : null;
              return <div className="demo-field" key={field.key}>
                <span className="demo-field__name" id={`${id}-label`}>{label(field)} {field.unit && <small>{field.unit}</small>}</span>
                {field.derived ? <span className="demo-field__generated">{bmi || text('derived')}</span> : <>
                  <select className="theme-select" aria-label={text('sourceLabel', { field: label(field) })} value={custom ? 'custom' : 'auto'} disabled={saving || !project.supported}
                    onChange={(event) => changeProject(project.code, (current) => ({ ...current, values: event.target.value === 'custom'
                      ? { ...current.values, [field.key]: '' }
                      : Object.fromEntries(Object.entries(current.values).filter(([key]) => key !== field.key)) }))}>
                    <option value="auto">{text('auto')}</option><option value="custom">{text('custom')}</option>
                  </select>
                  {custom ? <div className="demo-field__input"><input className="theme-input" type={field.input_type} step={field.step} min={field.min} max={field.max ?? 1000000}
                    aria-labelledby={`${id}-label`} aria-invalid={!!issue} aria-describedby={issue ? `${id}-error` : undefined} value={setting.values[field.key]}
                    disabled={saving || !project.supported} placeholder={text('valuePlaceholder')} onChange={(event) => changeProject(project.code, (current) => ({ ...current, values: { ...current.values, [field.key]: event.target.value } }))} />
                    {issue && <span id={`${id}-error`} className="demo-field__error">{text(issue, { precision: field.precision })}</span>}
                  </div> : <span className="demo-field__generated">{text('generatedAtMeasurement')}</span>}
                </>}
              </div>;
            })}
          </div>
          {showValidation && issues[project.code] && <p className="demo-field__error" role="alert">{text(issues[project.code])}</p>}
          <fieldset className="demo-project__usage" disabled={saving || !project.supported}><legend>{text('duration')}</legend>
            <label><input type="radio" name={`usage-${project.code}`} value="persistent" checked={setting.usage === 'persistent'} onChange={() => changeProject(project.code, (current) => ({ ...current, usage: 'persistent' }))} />{text('persistent')}</label>
            <label><input type="radio" name={`usage-${project.code}`} value="once" checked={setting.usage === 'once'} onChange={() => changeProject(project.code, (current) => ({ ...current, usage: 'once' }))} />{text('once', { project: label(project) })}</label>
          </fieldset>
        </section>;
      })}
    </div>
    <footer className="demo-settings__footer"><div className="demo-settings__summary" aria-live="polite"><strong>{text(dirty ? 'unsaved' : 'synced')}</strong>
      {dirty ? <ul>{changedCodes.map((code) => {
        const project = projects.find((p) => p.code === code);
        const setting = draft.projects[code];
        return <li key={code}>{project ? label(project) : text('unavailableProject')} · {setting ? text(setting.usage === 'once' ? 'onceShort' : 'persistent') : text('auto')}</li>;
      })}</ul> : <p>{text('persisted')}</p>}
    </div><div className="demo-settings__actions">
      <button className="theme-button theme-button--ghost" disabled={saving || !configured.length} onClick={() => setConfirmation('reset')}>{text('restoreAll')}</button>
      <button className="theme-button theme-button--primary" disabled={saving || !dirty || remoteChanged} onClick={save}>{text(saving ? 'saving' : 'save')}</button>
    </div></footer>
    {pickerOpen && createPortal(<div className="dialog-backdrop"><div ref={pickerRef} className="dialog-shell demo-project-picker" role="dialog" aria-modal="true" aria-labelledby="project-picker-title">
      <h2 id="project-picker-title">{text('pickTitle')}</h2><p>{text('pickHint')}</p>
      <div className="demo-project-picker__list">{projects.map((project) => {
        const added = !!draft.projects[project.code];
        return <label key={project.code} className={!project.supported || added ? 'is-disabled' : ''}>
          <input type="checkbox" disabled={!project.supported || added} checked={added || picked.includes(project.code)} onChange={(event) => setPicked((current) => event.target.checked ? [...current, project.code] : current.filter((code) => code !== project.code))} />
          <span>{label(project)}<small>{added ? text('alreadyAdded') : !project.supported ? text('notSupported') : project.fields.filter((f) => !f.derived).map(label).join(' · ')}</small></span>
        </label>;
      })}</div>
      <div className="dialog-shell__actions"><button className="theme-button theme-button--ghost" onClick={() => setPickerOpen(false)}>{text('cancel')}</button><button className="theme-button theme-button--primary" disabled={!picked.length} onClick={addProjects}>{text('addSelected')}</button></div>
    </div></div>, document.body)}
    <ConfirmDialog isOpen={!!confirmation} onClose={() => setConfirmation(null)} onConfirm={confirm}
      title={text(confirmation === 'reset' ? 'resetTitle' : 'discardTitle')}
      message={text(confirmation === 'reset' ? 'resetHint' : 'discardHint')}
      confirmText={text('confirm')} cancelText={text('cancel')} type="info" />
  </div>;
}
